import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { renderMarkdown } from "../components/ReplyEditor";
import {
  Inbox, Settings, Plus, Search, Send, ChevronDown, Check, CheckSquare, Square,
  UserPlus, MoreHorizontal, Quote, ThumbsUp, Filter, Trash2, Archive, Mail,
  Lock, X, Users, AlertCircle, ArrowUpDown, CornerDownRight, MessageSquare
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeData, setActiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  
  // New Message Modal
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [recipientInput, setRecipientInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [sendingNew, setSendingNew] = useState(false);
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(false);
  const [searchingMembers, setSearchingMembers] = useState(false);

  // UI Dropdowns & Selection
  const [folderOpen, setFolderOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [likes, setLikes] = useState({});

  const messagesEndRef = useRef(null);
  const replyInputRef = useRef(null);
  const subjectInputRef = useRef(null);
  const recipientInputRef = useRef(null);

  // Live member autocomplete search by @username
  useEffect(() => {
    if (!newModalOpen) {
      setRecipientSuggestions([]);
      setRecipientDropdownOpen(false);
      return;
    }
    const q = recipientInput.lstrip ? recipientInput.lstrip("@").trim() : recipientInput.replace(/^@+/, "").trim();
    const timer = setTimeout(async () => {
      setSearchingMembers(true);
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(q)}&limit=8`);
        const filtered = (res.data || []).filter(u => u.id !== user?.id && u.username !== user?.username);
        setRecipientSuggestions(filtered);
        setRecipientDropdownOpen(filtered.length > 0);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchingMembers(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [recipientInput, newModalOpen, user]);

  // Check URL query for direct DM trigger: /messages?dm=username
  useEffect(() => {
    const dmUser = searchParams.get("dm");
    if (dmUser) {
      setRecipientInput(dmUser);
      setNewModalOpen(true);
      searchParams.delete("dm");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load all threads
  const loadThreads = async () => {
    try {
      const res = await api.get("/dms/threads");
      setThreads(res.data || []);
      if (!activeThreadId && res.data && res.data.length > 0) {
        setActiveThreadId(res.data[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load active thread messages
  const loadThread = async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/dms/threads/${id}`);
      setActiveData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user) {
      loadThreads();
    }
  }, [user]);

  useEffect(() => {
    if (activeThreadId) {
      loadThread(activeThreadId);
    }
  }, [activeThreadId]);

  // Polling every 6s for real-time messages
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      loadThreads();
      if (activeThreadId) {
        loadThread(activeThreadId);
      }
    }, 6000);
    return () => clearInterval(interval);
  }, [user, activeThreadId]);

  // Auto-scroll on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeData?.messages?.length]);

  if (!user) {
    return (
      <div className="max-w-6xl mx-auto py-20 px-4 text-center">
        <div className="bg-[#0c1017] border border-white/10 rounded-2xl p-12 max-w-md mx-auto shadow-2xl">
          <Mail className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Mesaje Private</h2>
          <p className="text-zinc-400 text-sm mb-6">Trebuie să fii autentificat pentru a accesa sistemul de mesagerie privată.</p>
          <Link to="/login" className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black font-extrabold text-xs uppercase tracking-wider shadow-lg hover:brightness-110 transition-all inline-block">
            Autentificare
          </Link>
        </div>
      </div>
    );
  }

  // Handle Reply
  const handleSendReply = async (e) => {
    if (e) e.preventDefault();
    if (!replyText.trim() || !activeThreadId || sendingReply) return;
    setSendingReply(true);
    try {
      await api.post(`/dms/threads/${activeThreadId}/reply`, { content: replyText });
      setReplyText("");
      await loadThread(activeThreadId);
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la trimiterea mesajului");
    } finally {
      setSendingReply(false);
    }
  };

  // Handle New Thread / Conversation
  const handleStartNewConversation = async (e) => {
    e.preventDefault();
    if (!recipientInput.trim() || !messageInput.trim() || sendingNew) return;
    setSendingNew(true);
    try {
      const payload = {
        to_username: recipientInput.trim(),
        content: subjectInput.trim() ? `### ${subjectInput.trim()}\n\n${messageInput.trim()}` : messageInput.trim()
      };
      const res = await api.post("/dms/send", payload);
      toast.success("Mesaj trimis cu succes!");
      setNewModalOpen(false);
      setRecipientInput("");
      setSubjectInput("");
      setMessageInput("");
      await loadThreads();
      if (res.data?.thread_id) {
        setActiveThreadId(res.data.thread_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Utilizatorul nu a fost găsit sau a apărut o eroare");
    } finally {
      setSendingNew(false);
    }
  };

  // Handle Quote
  const handleQuoteMessage = (msg) => {
    const quoteFormatted = `> **${msg.sender_username}** a scris:\n> ${msg.content.replace(/\n/g, "\n> ")}\n\n`;
    setReplyText((prev) => prev + quoteFormatted);
    replyInputRef.current?.focus();
    toast.info("Citat adăugat în caseta de răspuns!");
  };

  // Handle Like Message
  const handleToggleLike = (msgId) => {
    setLikes((prev) => ({
      ...prev,
      [msgId]: !prev[msgId]
    }));
    toast.success("Reacție adăugată!");
  };

  // Filter threads by search
  const filteredThreads = threads.filter((t) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const otherName = t.other_user?.username?.toLowerCase() || "";
    const preview = t.last_message_preview?.toLowerCase() || "";
    return otherName.includes(q) || preview.includes(q);
  });

  const activeOtherUser = activeData?.other_user || threads.find(t => t.id === activeThreadId)?.other_user;
  const conversationTitle = activeOtherUser ? `Conversație cu ${activeOtherUser.username}` : "Mesagerie Privată";

  return (
    <div className="min-h-screen bg-[#07090e] text-zinc-200 py-6 px-3 sm:px-6 font-sans select-none">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* 1. TOP HEADER BAR */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#0d111a] border border-white/10 p-3 sm:p-4 rounded-xl shadow-lg">
          
          {/* Left: Folder Dropdown & Settings & New Message Button */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Folder Dropdown */}
            <div className="relative">
              <button
                onClick={() => setFolderOpen(!folderOpen)}
                className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white hover:text-amber-400 transition-colors"
              >
                <span>{selectedFolder === "inbox" ? "Inbox" : selectedFolder === "sent" ? "Trimise" : "Arhivă"}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${folderOpen ? "rotate-180 text-amber-400" : "text-zinc-400"}`} />
              </button>
              {folderOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-[#111622] border border-white/15 rounded-xl shadow-2xl py-1.5 z-50">
                  <button
                    onClick={() => { setSelectedFolder("inbox"); setFolderOpen(false); }}
                    className={`w-full px-4 py-2 text-xs font-semibold flex items-center justify-between hover:bg-white/10 ${selectedFolder === "inbox" ? "text-amber-400 bg-white/5" : "text-zinc-300"}`}
                  >
                    <span className="flex items-center gap-2"><Inbox className="w-3.5 h-3.5" /> Inbox</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10">{threads.length}</span>
                  </button>
                  <button
                    onClick={() => { setSelectedFolder("sent"); setFolderOpen(false); }}
                    className={`w-full px-4 py-2 text-xs font-semibold flex items-center justify-between hover:bg-white/10 ${selectedFolder === "sent" ? "text-amber-400 bg-white/5" : "text-zinc-300"}`}
                  >
                    <span className="flex items-center gap-2"><Send className="w-3.5 h-3.5" /> Mesaje trimise</span>
                  </button>
                  <button
                    onClick={() => { setSelectedFolder("archive"); setFolderOpen(false); }}
                    className={`w-full px-4 py-2 text-xs font-semibold flex items-center justify-between hover:bg-white/10 ${selectedFolder === "archive" ? "text-amber-400 bg-white/5" : "text-zinc-300"}`}
                  >
                    <span className="flex items-center gap-2"><Archive className="w-3.5 h-3.5" /> Arhivă</span>
                  </button>
                </div>
              )}
            </div>

            <button className="p-1.5 text-zinc-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors" title="Setări Mesagerie">
              <Settings className="w-4 h-4" />
            </button>

            {/* New Message CTA */}
            <button
              onClick={() => setNewModalOpen(true)}
              className="px-4 py-2 rounded-lg bg-[#1a202c] hover:bg-[#222a3a] border border-white/15 text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 shadow transition-all hover:border-amber-500/40"
            >
              <Plus className="w-3.5 h-3.5 text-amber-400" /> Scrie un mesaj nou
            </button>
          </div>

          {/* Right: Storage Usage Meter */}
          <div className="text-[11px] font-mono text-zinc-400 flex items-center gap-2">
            <span>Folosit <strong className="text-amber-400">4%</strong> din loc</span>
            <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 w-[4%]" />
            </div>
          </div>

        </div>

        {/* 2. MAIN MESSENGER SPLIT GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">

          {/* LEFT COLUMN: CONVERSATION LIST (4 COLS) */}
          <div className="lg:col-span-4 bg-[#0d111a] border border-white/10 rounded-xl overflow-hidden shadow-xl flex flex-col min-h-[640px]">
            
            {/* Action Subheader (Sort & Filter) */}
            <div className="p-3 border-b border-white/10 bg-black/30 flex items-center justify-between text-xs text-zinc-400">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectAll(!selectAll)}
                  className="text-zinc-400 hover:text-white"
                >
                  {selectAll ? <CheckSquare className="w-4 h-4 text-amber-400" /> : <Square className="w-4 h-4" />}
                </button>
                <ChevronDown className="w-3 h-3 text-zinc-500" />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSortOpen(!sortOpen)}
                  className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-[10px] font-extrabold uppercase tracking-wider text-zinc-300 flex items-center gap-1"
                >
                  SORTEAZĂ DUPĂ <ChevronDown className="w-3 h-3 text-zinc-500" />
                </button>
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-[10px] font-extrabold uppercase tracking-wider text-zinc-300 flex items-center gap-1"
                >
                  FILTREAZĂ DUPĂ <ChevronDown className="w-3 h-3 text-zinc-500" />
                </button>
              </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto divide-y divide-white/5 max-h-[520px]">
              {loading ? (
                <div className="p-8 text-center text-zinc-500 text-xs font-mono">Se încarcă mesajele...</div>
              ) : filteredThreads.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 text-xs">
                  <MessageSquare className="w-8 h-8 text-zinc-600 mx-auto mb-2 opacity-50" />
                  Nu există conversații în acest director.
                </div>
              ) : (
                filteredThreads.map((t) => {
                  const other = t.other_user || { username: "Utilizator", loadout: {} };
                  const isActive = activeThreadId === t.id;
                  const unread = t.unread_count > 0;

                  return (
                    <div
                      key={t.id}
                      onClick={() => setActiveThreadId(t.id)}
                      className={`p-3.5 flex items-start gap-3 cursor-pointer transition-all border-l-2 ${
                        isActive
                          ? "bg-[#141a27] border-amber-500 shadow-inner"
                          : "hover:bg-white/[0.03] border-transparent"
                      }`}
                    >
                      {/* Select checkbox */}
                      <div className="pt-2">
                        <input
                          type="checkbox"
                          checked={selectAll || selectedThreads.includes(t.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedThreads(prev =>
                              prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id]
                            );
                          }}
                          className="rounded bg-black/40 border-white/20 text-amber-500 focus:ring-0 w-3.5 h-3.5"
                        />
                      </div>

                      {/* Avatar */}
                      <Avatar user={other} size={40} />

                      {/* Content Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <h4 className={`text-xs font-bold truncate ${isActive ? "text-white" : "text-zinc-200"}`}>
                            {t.last_message_preview ? (t.last_message_preview.startsWith("### ") ? t.last_message_preview.split("\n")[0].replace("### ", "") : other.username) : other.username}
                          </h4>
                          {unread && (
                            <span className="w-4 h-4 rounded-full bg-red-600 text-white font-extrabold text-[9px] flex items-center justify-center shrink-0">
                              {t.unread_count}
                            </span>
                          )}
                        </div>

                        <div className="text-[11px] text-zinc-400 truncate mb-1">
                          <span className="text-zinc-500 font-semibold">{other.username}: </span>
                          <span>{t.last_message_preview || "Niciun mesaj încă..."}</span>
                        </div>

                        <div className="text-[10px] font-mono text-zinc-500">
                          {t.last_message_at ? formatDistanceToNow(new Date(t.last_message_at)) + " în urmă" : "Recent"}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Bottom Search & Messenger Status */}
            <div className="p-3 border-t border-white/10 bg-black/40 space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Căutați și în acest director..."
                  className="w-full bg-[#07090e] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500"
                />
                <Settings className="w-3.5 h-3.5 text-zinc-500 absolute right-2.5 top-2.5" />
              </div>
              <div className="text-right">
                <button className="text-[10px] text-zinc-500 hover:text-zinc-300 font-mono">
                  Opriți mesagerul meu
                </button>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: ACTIVE THREAD VIEW (8 COLS) */}
          <div className="lg:col-span-8 bg-[#0d111a] border border-white/10 rounded-xl overflow-hidden shadow-xl flex flex-col min-h-[640px]">
            
            {activeData ? (
              <>
                {/* 1. THREAD HEADER */}
                <div className="p-5 border-b border-white/10 bg-black/40">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-black text-white tracking-wide">
                        {conversationTitle}
                      </h2>
                      <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-1">
                        <Users className="w-3.5 h-3.5 text-amber-400" />
                        <span>2 utilizatori activi în această conversație (inclusiv tu)</span>
                      </div>
                    </div>

                    {/* Options Dropdown Button */}
                    <div className="relative">
                      <button
                        onClick={() => setOptionsOpen(!optionsOpen)}
                        className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-1.5 transition-all"
                      >
                        Opțiuni <ChevronDown className={`w-3 h-3 ${optionsOpen ? "rotate-180" : ""}`} />
                      </button>
                      {optionsOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-[#111622] border border-white/15 rounded-xl shadow-2xl py-1.5 z-50 text-left">
                          <button
                            onClick={() => { setOptionsOpen(false); toast.info("Conversație marcată ca necitită"); }}
                            className="w-full px-3.5 py-2 text-xs text-zinc-300 hover:text-white hover:bg-white/10 text-left flex items-center gap-2"
                          >
                            <Mail className="w-3.5 h-3.5" /> Marchează ca necitit
                          </button>
                          <button
                            onClick={() => { setOptionsOpen(false); toast.warning("Ai părăsit conversația"); }}
                            className="w-full px-3.5 py-2 text-xs text-red-400 hover:bg-red-500/10 text-left flex items-center gap-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Părăsește conversația
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Participants Row */}
                  <div className="flex items-center gap-3 flex-wrap pt-2">
                    {/* Current User Card */}
                    <div className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.03] border border-white/5 pr-4">
                      <Avatar user={user} size={32} />
                      <div className="text-left leading-tight">
                        <span className="text-xs font-bold text-white block">{user.username}</span>
                        <span className="text-[10px] text-emerald-400 font-mono">Citește: Acum</span>
                      </div>
                    </div>

                    {/* Other User Card */}
                    {activeOtherUser && (
                      <div className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.03] border border-white/5 pr-4">
                        <Avatar user={activeOtherUser} size={32} />
                        <div className="text-left leading-tight">
                          <span className="text-xs font-bold text-white flex items-center gap-1">
                            {activeOtherUser.username} <ChevronDown className="w-2.5 h-2.5 text-zinc-500" />
                          </span>
                          <span className="text-[10px] text-zinc-400 font-mono">Citește: 4 ore în urmă</span>
                        </div>
                      </div>
                    )}

                    {/* Add Participant Button */}
                    <button
                      onClick={() => toast.info("Funcția de grup multi-user este activă pe server.")}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                      title="Adaugă participanți"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 2. MESSAGES FEED */}
                <div className="flex-1 p-5 overflow-y-auto space-y-4 max-h-[480px]">
                  {(activeData.messages || []).map((msg, idx) => {
                    const isOwn = msg.sender_id === user.id;
                    const senderUser = isOwn ? user : activeOtherUser;
                    const hasLiked = likes[msg.id];

                    return (
                      <div
                        key={msg.id || idx}
                        className="p-4 sm:p-5 rounded-xl bg-[#090d14]/90 border border-white/10 shadow-lg relative group transition-all hover:border-white/20"
                      >
                        {/* Header: Author + Timestamp + Actions */}
                        <div className="flex items-start justify-between gap-3 mb-3 border-b border-white/5 pb-3">
                          <div className="flex items-center gap-3">
                            <Avatar user={senderUser} size={38} />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Username user={senderUser} className="text-sm font-bold" />
                                {senderUser?.loadout?.banner_badge && (
                                  <VipBadge tier={senderUser.loadout.banner_badge} />
                                )}
                              </div>
                              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                                Începerea conversației: {msg.created_at ? formatDistanceToNow(new Date(msg.created_at)) + " în urmă" : "Recent"}
                              </div>
                            </div>
                          </div>

                          <div className="relative">
                            <button className="text-zinc-500 hover:text-zinc-300 p-1">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Message Body with Markdown */}
                        <div
                          className="text-sm text-zinc-200 prose prose-invert max-w-none space-y-2 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content || "") }}
                        />

                        {/* Bottom Message Actions */}
                        <div className="flex items-center justify-start gap-2 pt-3 mt-3 border-t border-white/5 text-xs">
                          {/* Like Button */}
                          <button
                            onClick={() => handleToggleLike(msg.id)}
                            className={`px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 border text-[11px] font-bold flex items-center gap-1 transition-all ${
                              hasLiked ? "border-amber-500/50 text-amber-400 bg-amber-500/10" : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                          >
                            <Plus className="w-3 h-3" /> {hasLiked ? "1" : ""}
                          </button>

                          {/* Quote Button */}
                          <button
                            onClick={() => handleQuoteMessage(msg)}
                            className="px-3 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] font-bold text-zinc-300 hover:text-white flex items-center gap-1.5 transition-all"
                          >
                            <Quote className="w-3 h-3 text-amber-400" /> Citat
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* 3. BOTTOM REPLY BOX */}
                <form onSubmit={handleSendReply} className="p-4 border-t border-white/10 bg-black/40 flex items-center gap-3">
                  <Avatar user={user} size={38} />
                  <div className="flex-1 relative">
                    <input
                      ref={replyInputRef}
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Adauga un raspuns..."
                      className="w-full bg-[#07090e] border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!replyText.trim() || sendingReply}
                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-extrabold text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 shadow-lg flex items-center gap-1.5 transition-all"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-zinc-500">
                <MessageSquare className="w-12 h-12 text-zinc-600 mb-3 opacity-40" />
                <h3 className="text-base font-bold text-zinc-400">Nicio conversație selectată</h3>
                <p className="text-xs text-zinc-600 mt-1 max-w-sm">Selectează o conversație din lista din stânga sau inițiază un mesaj nou.</p>
                <button
                  onClick={() => setNewModalOpen(true)}
                  className="mt-4 px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-400 font-bold text-xs uppercase tracking-wider hover:bg-amber-500 hover:text-black transition-all"
                >
                  Scrie un mesaj nou
                </button>
              </div>
            )}

          </div>

        </div>

      </div>

      {/* 3. MODAL: SCRIE UN MESAJ NOU */}
      {newModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#0e131d] border border-white/15 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-400" /> Scrie un mesaj nou
              </h3>
              <button onClick={() => setNewModalOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleStartNewConversation} className="space-y-3.5">
              {/* MEMBER AUTOCOMPLETE RECIPIENT INPUT */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] uppercase font-bold text-zinc-400 tracking-wider block">
                    Destinatar (Caută prin @nume sau username)
                  </label>
                  <span className="text-[10px] text-amber-400 font-mono">ex: @root, @Mint, @Admin</span>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-zinc-500 font-bold text-sm">@</span>
                  <input
                    ref={recipientInputRef}
                    type="text"
                    required
                    value={recipientInput.startsWith("@") ? recipientInput.slice(1) : recipientInput}
                    onChange={(e) => {
                      setRecipientInput(e.target.value);
                      setRecipientDropdownOpen(true);
                    }}
                    onFocus={() => {
                      if (recipientSuggestions.length > 0) setRecipientDropdownOpen(true);
                    }}
                    placeholder="tastați numele membrului..."
                    className="w-full bg-black/50 border border-white/15 rounded-lg pl-8 pr-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 font-medium placeholder-zinc-500 transition-colors"
                  />
                  {searchingMembers && (
                    <div className="absolute right-3 top-2.5 w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>

                {/* AUTOCOMPLETE DROPDOWN */}
                {recipientDropdownOpen && recipientSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1.5 bg-[#0c1017] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-[100] max-h-60 overflow-y-auto divide-y divide-white/5 animate-in fade-in">
                    <div className="px-3 py-1.5 bg-black/40 text-[10px] font-mono text-zinc-500 uppercase tracking-wider flex items-center justify-between">
                      <span>Membri găsiți ({recipientSuggestions.length})</span>
                      <span className="text-amber-400">Click pentru selecție</span>
                    </div>
                    {recipientSuggestions.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setRecipientInput(u.username);
                          setRecipientDropdownOpen(false);
                          subjectInputRef.current?.focus();
                          toast.success(`Destinatar selectat: @${u.username}`);
                        }}
                        className="w-full px-3.5 py-2.5 flex items-center justify-between gap-3 hover:bg-white/10 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar user={u} size={32} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors truncate">
                                @{u.username}
                              </span>
                              {u.loadout?.banner_badge && u.loadout.banner_badge !== "none" && (
                                <VipBadge tier={u.loadout.banner_badge} />
                              )}
                            </div>
                            <div className="text-[10px] text-zinc-400 font-mono flex items-center gap-1">
                              <span style={{ color: u.group_color || '#ea580c' }} className="font-bold">
                                {u.group_name || (u.role === "root" ? "Root" : u.is_vip ? "FORUM VIP" : "Membru")}
                              </span>
                              <span>· {u.points || 0} pts</span>
                            </div>
                          </div>
                        </div>

                        <span className="text-[10px] font-bold px-2 py-1 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 group-hover:bg-amber-500 group-hover:text-black transition-colors shrink-0">
                          Selectează
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">Subiect</label>
                <input
                  ref={subjectInputRef}
                  type="text"
                  value={subjectInput}
                  onChange={(e) => setSubjectInput(e.target.value)}
                  placeholder="ex: Salutare / Propunere meci"
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase font-bold text-zinc-400 tracking-wider block mb-1">Mesaj</label>
                <textarea
                  required
                  rows={5}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Scrie mesajul tău aici... (suportă formatare Markdown și linkuri)"
                  className="w-full bg-black/50 border border-white/10 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setNewModalOpen(false)}
                  className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold uppercase tracking-wider text-zinc-400"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  disabled={sendingNew || !recipientInput.trim() || !messageInput.trim()}
                  className="px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black font-extrabold text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 shadow-lg flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" /> {sendingNew ? "Se trimite..." : "Trimite mesaj"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
